import { useEffect, useState, type FormEvent } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';

export interface EditTextModalProps {
    open: boolean;
    title: string;
    label?: string;
    initialValue: string;
    minLength?: number;
    maxLength?: number;
    confirmLabel?: string;
    cancelLabel?: string;
    onSubmit: (value: string) => void;
    onCancel: () => void;
}

export default function EditTextModal({
    open,
    title,
    label,
    initialValue,
    minLength = 3,
    maxLength = 50,
    confirmLabel = 'Save',
    cancelLabel = 'Cancel',
    onSubmit,
    onCancel,
}: EditTextModalProps) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        if (!open) return;
        setValue(initialValue);
    }, [open, initialValue]);

    const trimmed = value?.trim() ?? '';
    const error = trimmed.length < minLength
        ? `Must be at least ${minLength} characters`
        : trimmed.length > maxLength
            ? `Must be at most ${maxLength} characters`
            : null;

    function handleSubmit(e: FormEvent) {
        e.preventDefault();
        if (error) return;
        onSubmit(trimmed);
    }

    return (
        <Modal
            open={open}
            onClose={onCancel}
            title={title}
            size="sm"
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Input
                    id="edit-text-modal-input"
                    label={label}
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    maxLength={maxLength + 20}
                    error={error ?? undefined}
                />
                <div className="flex gap-3 justify-end mt-2">
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={onCancel}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={!!error}
                    >
                        {confirmLabel}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
